/**
 * Security W43 M13 — the ONE way the widget turns an answer into HTML.
 *
 * The widget runs on a CUSTOMER's site, so `marked` output inserted as
 * innerHTML was live script on their origin: `<img onerror=…>` or a
 * `javascript:` link in poisoned content or a prompt-injected answer ran with
 * the customer's cookies. DOMPurify removes the active surface.
 *
 * Same policy as the admin SPA's renderMarkdown (apps/admin/src/lib/markdown.ts,
 * W43 L12): no forms/inputs/buttons, and nothing that LOADS from another origin
 * by itself (img src, srcset, CSS url, svg image, poster). Links stay — they
 * need a click. Kept as a copy on purpose: the widget is a standalone npm
 * build outside the pnpm workspace and must not pull in the admin bundle.
 * Change both together.
 */
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const purifier = DOMPurify(window);
const LOADING_ATTRS = ['src', 'href', 'xlink:href'] as const;
const LINK_TAGS = new Set(['A', 'AREA']);

function isSameOrigin(url: string): boolean {
  const base = window.location.href;
  if (!URL.canParse(url, base)) return false;
  return new URL(url, base).origin === window.location.origin;
}

purifier.addHook('afterSanitizeAttributes', (node) => {
  for (const attr of LOADING_ATTRS) {
    if (attr !== 'src' && LINK_TAGS.has(node.nodeName)) continue;
    const value = node.getAttribute(attr);
    if (value !== null && !isSameOrigin(value)) node.removeAttribute(attr);
  }
});

const PURIFY_CONFIG = {
  FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'option', 'optgroup', 'fieldset', 'datalist'],
  FORBID_ATTR: ['style', 'srcset', 'poster', 'background', 'action', 'formaction', 'ping'],
};

export function renderAnswerHtml(markdown: string): string {
  const html = marked.parse(markdown, { async: false }) as string;
  return purifier.sanitize(html, PURIFY_CONFIG);
}
